/**
 * Pure-logic helpers for the workspace tabs Vuex module.
 *
 * Extracted from tabs.js so node:test can import + cover the per-tab
 * history-stack mutation logic without pulling in Vuex itself. tabs.js
 * mutations are thin wrappers that locate the tab object and delegate
 * here.
 */

export const MAX_TAB_HISTORY = 30;

/**
 * Extract the module base path from a full route path.
 * /erp/ledgers/create → /erp/ledgers
 * /erp/ledgers/123    → /erp/ledgers
 * /erp/vouchers?type=Sales → /erp/vouchers
 */
export function getModuleKey(fullPath) {
  let path = fullPath.split('?')[0].split('#')[0];
  path = path.replace(/\/$/, '');

  const segments = path.split('/');
  const actionSegments = new Set(['create', 'details', 'balance', 'breakup', 'consolidated-stock']);

  while (segments.length > 2) {
    const last = segments[segments.length - 1];
    if (/^\d+$/.test(last) || actionSegments.has(last)) {
      segments.pop();
    } else {
      break;
    }
  }

  return segments.join('/');
}

/**
 * Push a path onto a tab's per-tab history stack with branch-aware logic:
 *   - duplicate of current position    → no-op
 *   - matches history[idx + 1]         → forward step (just bump idx)
 *   - matches history[idx - 1]         → backward step (just decrement idx)
 *   - otherwise                        → genuine new nav: truncate forward
 *                                        branch and append, capped to
 *                                        MAX_TAB_HISTORY entries.
 *
 * Mutates `tab` in place. Initializes `history` and `idx` if missing.
 */
export function pushTabHistory(tab, path) {
  if (!Array.isArray(tab.history)) tab.history = [];
  if (typeof tab.idx !== 'number') tab.idx = tab.history.length - 1;

  if (tab.history[tab.idx] === path) return;
  if (tab.history[tab.idx + 1] === path) {
    tab.idx += 1;
    return;
  }
  if (tab.idx > 0 && tab.history[tab.idx - 1] === path) {
    tab.idx -= 1;
    return;
  }

  tab.history = tab.history.slice(0, tab.idx + 1);
  tab.history.push(path);
  if (tab.history.length > MAX_TAB_HISTORY) {
    tab.history = tab.history.slice(tab.history.length - MAX_TAB_HISTORY);
  }
  tab.idx = tab.history.length - 1;
}

/** Move the tab's idx by `delta` (clamped to [0, history.length - 1]). */
export function moveTabIdx(tab, delta) {
  if (!Array.isArray(tab.history)) return;
  const next = (tab.idx ?? 0) + delta;
  if (next < 0 || next >= tab.history.length) return;
  tab.idx = next;
  tab.path = tab.history[next];
}

/** Set the tab's idx to an absolute position (rejected if out of bounds). */
export function setTabIdx(tab, idx) {
  if (!Array.isArray(tab.history)) return;
  if (idx < 0 || idx >= tab.history.length) return;
  tab.idx = idx;
  tab.path = tab.history[idx];
}
